import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { generateQuotePdf, generateProposalPdf } from "../lib/pdf";
import AcceptProposal from "../components/AcceptProposal";
import { renderMarkdown } from "../lib/markdown";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Download, Sparkles, Loader2, CreditCard, CheckCircle2, Mail, FileText, Globe, Send, Copy, CalendarCheck } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const mdy = (s) => { if (!s) return ""; const p = String(s).split("-"); return p.length === 3 ? `${p[1]}/${p[2]}/${p[0]}` : s; };

const MODELS = [
  { id: "claude", label: "Claude Sonnet 4.6" },
  { id: "gpt", label: "GPT-5.4-mini" },
  { id: "gemini", label: "Gemini 3.1 Pro" },
];

export default function QuoteDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [model, setModel] = useState("claude");
  const [generating, setGenerating] = useState(false);
  const [paying, setPaying] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    api.get(`/quotes/${id}`).then(({ data }) => setQuote(data)).catch(() => navigate("/dashboard")).finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  // Poll Stripe payment status after returning from checkout
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setVerifying(true);
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          const { data: q } = await api.get(`/quotes/${id}`);
          setQuote(q);
          setVerifying(false);
          toast.success("Deposit paid — quote accepted!");
          window.history.replaceState({}, "", `/quotes/${id}`);
          return;
        }
        if (data.status === "expired") { setVerifying(false); toast.error("Payment session expired."); return; }
        if (attempts++ < 6) { setTimeout(poll, 2000); } else { setVerifying(false); }
      } catch {
        if (attempts++ < 6) { setTimeout(poll, 2000); } else { setVerifying(false); }
      }
    };
    poll();
  }, [params, id]);

  const depositAmount = quote
    ? Math.round((quote.one_time_total ? quote.one_time_total * 0.3 : quote.monthly_total) * 100) / 100
    : 0;

  const payDeposit = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/quotes/${id}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setPaying(false);
    }
  };

  const [emailing, setEmailing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [siteUrl, setSiteUrl] = useState("");
  const [catalog, setCatalog] = useState({ packages: [], addons: [] });
  useEffect(() => { api.get("/catalog").then(({ data }) => setCatalog(data)).catch(() => {}); }, []);
  const [onboarding, setOnboarding] = useState(null);
  const [sendingOb, setSendingOb] = useState(false);
  useEffect(() => {
    if (user?.role === "admin") {
      api.get(`/onboarding?quote_id=${id}`).then(({ data }) => setOnboarding(data[0] || null)).catch(() => {});
    }
  }, [id, user]);
  const sendOnboarding = async () => {
    setSendingOb(true);
    try {
      const { data } = await api.post("/onboarding", { client_name: quote.client_name, client_email: quote.client_email, quote_id: id });
      setOnboarding(data);
      toast.success(`Onboarding sent to ${quote.client_email}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send onboarding"); }
    finally { setSendingOb(false); }
  };
  const copyObLink = () => { navigator.clipboard.writeText(`${window.location.origin}/onboarding/${onboarding.token}`); toast.success("Onboarding link copied"); };

  // Denefits financing (admin)
  const [denefitsAmount, setDenefitsAmount] = useState("");
  const [denefitsTerm, setDenefitsTerm] = useState(12);
  const [denefitsBusy, setDenefitsBusy] = useState(false);
  const createDenefits = async () => {
    const amt = parseFloat(denefitsAmount);
    if (!amt || amt <= 0) { toast.error("Enter a financing amount"); return; }
    setDenefitsBusy(true);
    try {
      const { data } = await api.post(`/quotes/${id}/denefits`, { amount: amt, term_months: Number(denefitsTerm) });
      setQuote((q) => ({ ...q, denefits: data }));
      toast.success(data.mock ? "Financing link created (mock — add Denefits credentials to go live)" : "Denefits financing created");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create financing"); }
    finally { setDenefitsBusy(false); }
  };
  const removeDenefits = async () => {
    try { await api.delete(`/quotes/${id}/denefits`); setQuote((q) => ({ ...q, denefits: null })); }
    catch { toast.error("Could not remove financing"); }
  };
  const copyDenefitsLink = () => { navigator.clipboard.writeText(quote.denefits.hosted_checkout_url); toast.success("Financing link copied"); };
  const [sendOpen, setSendOpen] = useState(false);
  const [pdfEmail, setPdfEmail] = useState("");
  const [pdfMsg, setPdfMsg] = useState("");
  const [sendingPdf, setSendingPdf] = useState(false);
  const doSendPdf = async () => {
    if (!pdfEmail.trim()) { toast.error("Enter a recipient email"); return; }
    setSendingPdf(true);
    try {
      await api.post(`/onboarding/${onboarding.id}/send`, { to_email: pdfEmail.trim(), message: pdfMsg });
      toast.success(`Questionnaire PDF sent to ${pdfEmail.trim()}`);
      setSendOpen(false); setPdfEmail(""); setPdfMsg("");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setSendingPdf(false); }
  };
  const recAddonNames = (quote?.website_rec_addons || [])
    .map((id) => catalog.addons.find((a) => a.id === id))
    .filter(Boolean);
  const recPackage = quote?.website_rec_package
    ? catalog.packages.find((p) => p.id === quote.website_rec_package)
    : null;
  const buildUpsellQuote = () => {
    const addons = {};
    (quote.website_rec_addons || []).forEach((id) => { addons[id] = true; });
    const prefill = {
      pkg: quote.website_rec_package || quote.package_id || null,
      addons,
      from_audit: true,
      form: {
        client_name: quote.client_name || "",
        client_email: quote.client_email || "",
        company: quote.company || "",
        needs: quote.needs || "",
        website_url: quote.website_url || "",
      },
    };
    sessionStorage.setItem("builderPrefill", JSON.stringify(prefill));
    navigate("/build");
  };
  const analyzeWebsite = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/quotes/${id}/analyze-website`, { website_url: siteUrl || quote.website_url || "" });
      setQuote(data);
      toast.success("Website analyzed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Website analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };
  const sendEmail = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post(`/quotes/${id}/email`);
      toast.success(`Email on its way to ${data.to}…`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally {
      setEmailing(false);
    }
  };

  const [proposalPdfLoading, setProposalPdfLoading] = useState(false);
  const downloadProposal = async () => {
    setProposalPdfLoading(true);
    try {
      let q = quote;
      if (!q.proposal) {
        toast.message("Writing your proposal with AI…");
        const { data } = await api.post(`/quotes/${id}/proposal`, { model });
        setQuote(data);
        q = data;
      }
      await generateProposalPdf(q);
      toast.success("Formal proposal downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create proposal PDF");
    } finally {
      setProposalPdfLoading(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/quotes/${id}/proposal`, { model });
      setQuote(data);
      toast.success("AI proposal generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const setStatus = async (status) => {
    const { data } = await api.patch(`/quotes/${id}/status`, { status });
    setQuote(data);
    if (status === "sent") {
      toast.success(`Marked sent — emailing ${data.client_email}…`);
    } else {
      toast.success(`Marked as ${status}`);
    }
  };

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-20 font-mono text-sm text-zinc-500">Loading…</div>;
  if (!quote) return null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="quote-detail-page">
      <button onClick={() => navigate(user?.role === "admin" ? "/admin" : "/dashboard")} className="flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-zinc-500 hover:text-primary mb-6">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display font-black text-4xl tracking-tighter">#{quote.id.slice(-8).toUpperCase()}</h1>
            <StatusBadge status={quote.status} />
          </div>
          <p className="mt-2 text-zinc-600">{quote.client_name} · {quote.client_email}{quote.company ? ` · ${quote.company}` : ""}</p>
          {quote.expected_completion && (
            <p data-testid="quote-expected-completion" className="mt-1 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-primary">
              <CalendarCheck className="h-3.5 w-3.5" /> Expected completion {mdy(quote.expected_completion)}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button onClick={sendEmail} disabled={emailing} data-testid="email-quote-btn" variant="outline" className="rounded-none border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800 font-mono uppercase tracking-widest text-xs">
            {emailing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1" /> Email Client</>}
          </Button>
          <Button onClick={() => generateQuotePdf(quote)} data-testid="download-pdf-btn" variant="outline" className="rounded-none border-red-300 bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800 font-mono uppercase tracking-widest text-xs">
            <Download className="h-4 w-4 mr-1" /> Download PDF
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 items-start">
        {/* Line items */}
        <div className="lg:col-span-5">
          <div className="border border-zinc-900 bg-white shadow-[6px_6px_0px_0px_rgba(10,10,10,1)]">
            <div className="bg-zinc-900 text-white px-5 py-3 font-display font-bold">Quote Breakdown</div>
            <div className="p-5">
              <ul className="space-y-3">
                {quote.line_items.map((li, i) => (
                  <li key={i} className="flex justify-between text-sm">
                    <span className="text-zinc-700">{li.name}</span>
                    <span className="font-mono">{fmt(li.price)}{li.billing === "monthly" ? "/mo" : ""}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-zinc-200 mt-4 pt-4 space-y-1">
                <div className="flex justify-between">
                  <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">One-time</span>
                  <span className="font-display font-black text-xl">{fmt(quote.one_time_total)}</span>
                </div>
                {quote.monthly_total > 0 && (
                  <div className="flex justify-between">
                    <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">Monthly</span>
                    <span className="font-display font-bold text-lg text-primary">{fmt(quote.monthly_total)}/mo</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Status actions (staff-only manual controls) */}
          {["admin", "salesperson"].includes(user?.role) && (
            <div className="mt-4 flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" data-testid="mark-sent-btn" onClick={() => setStatus("sent")} className="rounded-none border-zinc-300 font-mono text-xs uppercase">Mark Sent</Button>
              <Button size="sm" variant="outline" data-testid="mark-accepted-btn" onClick={() => setStatus("accepted")} className="rounded-none border-green-300 text-green-700 font-mono text-xs uppercase">Accept</Button>
              <Button size="sm" variant="outline" data-testid="mark-rejected-btn" onClick={() => setStatus("rejected")} className="rounded-none border-red-300 text-red-700 font-mono text-xs uppercase">Reject</Button>
            </div>
          )}

          {/* Accept proposal with online signature */}
          <div className="mt-4">
            <AcceptProposal quote={quote} onAccepted={(q) => setQuote(q)} />
          </div>

          {/* Deposit to accept */}
          <div className="mt-4 border border-zinc-900 bg-white p-5" data-testid="deposit-panel">
            {quote.deposit_paid ? (
              <div className="flex items-center gap-3" data-testid="deposit-paid-state">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
                <div>
                  <p className="font-display font-bold">Deposit Paid</p>
                  <p className="font-mono text-xs text-zinc-500">{fmt(quote.deposit_amount || depositAmount)} received · quote accepted</p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Deposit to accept (30%)</p>
                    <p className="font-display font-black text-2xl tracking-tighter">{fmt(depositAmount)}</p>
                  </div>
                  <CreditCard className="h-7 w-7 text-primary" />
                </div>
                <Button
                  onClick={payDeposit}
                  disabled={paying || verifying}
                  data-testid="pay-deposit-btn"
                  className="w-full mt-4 rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest h-11"
                >
                  {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : verifying ? "Verifying payment…" : "Pay Deposit & Accept"}
                </Button>
                <p className="mt-2 text-[11px] text-zinc-400 text-center">Secure checkout via Stripe. Remaining balance billed on delivery.</p>
              </>
            )}
          </div>

          {/* Denefits financing (admin) */}
          {user?.role === "admin" && (
            <div className="mt-4 border border-zinc-200 bg-white p-5" data-testid="denefits-panel">
              <div className="flex items-center justify-between mb-3">
                <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Financing · Denefits</p>
                <span className="font-mono text-[9px] uppercase tracking-wider bg-primary/10 text-primary px-1.5 py-0.5">Pay monthly</span>
              </div>
              {quote.denefits ? (
                <div data-testid="denefits-active">
                  <p className="font-display font-black text-2xl tracking-tighter">{fmt(quote.denefits.financed_amount)}</p>
                  <p className="font-mono text-xs text-zinc-500 mt-0.5">~{fmt(quote.denefits.monthly_estimate)}/mo × {quote.denefits.term_months} mo</p>
                  {quote.denefits.mock && (
                    <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 px-2 py-1" data-testid="denefits-mock-notice">Mock link — add Denefits API credentials to issue real contracts.</p>
                  )}
                  <div className="flex gap-2 mt-3">
                    <Button onClick={copyDenefitsLink} data-testid="denefits-copy-link" variant="outline" className="flex-1 rounded-none border-zinc-300 font-mono uppercase tracking-widest text-xs"><Copy className="h-3.5 w-3.5 mr-1" /> Copy link</Button>
                    <Button onClick={removeDenefits} data-testid="denefits-remove" variant="outline" className="rounded-none border-zinc-300 text-zinc-500 hover:text-red-600 hover:border-red-300 text-xs">Remove</Button>
                  </div>
                </div>
              ) : (
                <div data-testid="denefits-create">
                  <div className="flex gap-2">
                    <input type="number" min="1" step="0.01" data-testid="denefits-amount" value={denefitsAmount} onChange={(e) => setDenefitsAmount(e.target.value)} placeholder={`Amount (e.g. ${Math.round((quote.one_time_total || 0) - depositAmount)})`} className="flex-1 min-w-0 border border-zinc-300 px-2 py-2 text-sm outline-none focus:border-primary" />
                    <select data-testid="denefits-term" value={denefitsTerm} onChange={(e) => setDenefitsTerm(e.target.value)} className="border border-zinc-300 px-2 py-2 text-sm outline-none focus:border-primary">
                      {[6, 12, 18, 24, 36].map((m) => <option key={m} value={m}>{m} mo</option>)}
                    </select>
                  </div>
                  <Button onClick={createDenefits} disabled={denefitsBusy} data-testid="denefits-create-btn" className="w-full mt-3 rounded-none bg-zinc-900 hover:bg-zinc-700 text-white font-mono uppercase tracking-widest h-10 text-xs">
                    {denefitsBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create financing link"}
                  </Button>
                  <p className="mt-2 text-[11px] text-zinc-400 text-center">Lets the client pay over time. Shown on their quote page.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* AI proposal */}
        <div className="lg:col-span-7">
          <div className="border border-zinc-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-zinc-200">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <h3 className="font-display font-bold text-lg">AI Proposal</h3>
              </div>
              {user?.role === "admin" && (
                <div className="flex items-center gap-2">
                  <Select value={model} onValueChange={setModel}>
                    <SelectTrigger data-testid="ai-model-select" className="rounded-none border-zinc-300 h-9 w-[160px] text-xs font-mono">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MODELS.map((m) => (
                        <SelectItem key={m.id} value={m.id} data-testid={`model-opt-${m.id}`}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={generate} disabled={generating} data-testid="generate-proposal-btn" className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest text-xs h-9">
                    {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : quote.proposal ? "Regenerate" : "Generate"}
                  </Button>
                </div>
              )}
            </div>
            <div className="p-6">
              {generating ? (
                <div className="py-12 text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                  <p className="mt-3 font-mono text-xs uppercase tracking-widest text-zinc-500">Crafting your proposal…</p>
                </div>
              ) : quote.proposal ? (
                <div
                  data-testid="proposal-content"
                  className="proposal-md text-sm text-zinc-800"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(quote.proposal) }}
                />
              ) : (
                <div className="py-12 text-center">
                  <Sparkles className="h-8 w-8 text-zinc-300 mx-auto" />
                  <p className="mt-3 text-zinc-600">{user?.role === "admin" ? "Generate a tailored, AI-written proposal for this quote." : "Your detailed proposal will appear here shortly."}</p>
                  {user?.role === "admin" && <p className="mt-1 font-mono text-xs text-zinc-400">Pick a model and hit Generate.</p>}
                </div>
              )}
            </div>
            <div className="px-6 pb-6 border-t border-zinc-100 pt-4">
              <Button onClick={downloadProposal} disabled={proposalPdfLoading || generating} data-testid="proposal-pdf-btn" className="w-full rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs h-11">
                {proposalPdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileText className="h-4 w-4 mr-2" /> {quote.proposal ? "Download Formal Proposal" : "Create Formal Proposal"}</>}
              </Button>
              <p className="mt-2 text-[11px] text-zinc-400 text-center">Branded multi-page PDF with AI scope + pricing.</p>
            </div>
          </div>
        </div>
      </div>

      {user?.role === "admin" && (
        <div className="mt-8 border border-zinc-900 bg-white p-5 flex flex-wrap items-center justify-between gap-3" data-testid="quote-onboarding-panel">
          <div>
            <h3 className="font-display font-bold text-lg">Client Onboarding</h3>
            {onboarding ? (
              <p className="text-sm text-zinc-500 mt-0.5">Status: <span className="font-medium text-zinc-800 capitalize">{onboarding.status.replace("_", " ")}</span></p>
            ) : (
              <p className="text-sm text-zinc-500 mt-0.5">Send {quote.client_name} the website-development onboarding questionnaire.</p>
            )}
          </div>
          {onboarding ? (
            <div className="flex gap-2">
              <Button onClick={copyObLink} data-testid="quote-ob-copy-btn" variant="outline" className="rounded-none border-zinc-900 font-mono uppercase tracking-widest text-xs"><Copy className="h-4 w-4 mr-1" /> Copy link</Button>
              <Button onClick={() => setSendOpen(true)} data-testid="quote-ob-send-pdf-btn" variant="outline" className="rounded-none border-zinc-900 font-mono uppercase tracking-widest text-xs"><Send className="h-4 w-4 mr-1" /> Send to…</Button>
              <Button onClick={() => navigate("/admin/onboarding")} variant="outline" className="rounded-none border-zinc-900 font-mono uppercase tracking-widest text-xs">View responses</Button>
            </div>
          ) : (
            <Button onClick={sendOnboarding} disabled={sendingOb} data-testid="quote-send-onboarding-btn" className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest text-xs">
              <Send className="h-4 w-4 mr-1" /> {sendingOb ? "Sending…" : "Send onboarding"}
            </Button>
          )}
        </div>
      )}

      <Dialog open={sendOpen} onOpenChange={(o) => !o && setSendOpen(false)}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle>Send Questionnaire To</DialogTitle>
            <DialogDescription>Email {quote.client_name}'s completed onboarding PDF to a third party (developer, designer, partner).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest">Recipient email</Label>
              <Input data-testid="quote-send-pdf-email" type="email" value={pdfEmail} onChange={(e) => setPdfEmail(e.target.value)} placeholder="dev@partner.com" className="mt-1.5 rounded-none border-zinc-300" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest">Message (optional)</Label>
              <Textarea data-testid="quote-send-pdf-message" value={pdfMsg} onChange={(e) => setPdfMsg(e.target.value)} placeholder="Add a short note…" className="mt-1.5 rounded-none border-zinc-300 min-h-[80px]" />
            </div>
            <Button onClick={doSendPdf} disabled={sendingPdf} data-testid="quote-send-pdf-confirm" className="w-full rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest h-11">
              <Send className="h-4 w-4 mr-1.5" /> {sendingPdf ? "Sending…" : "Send PDF"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {user?.role === "admin" && (
        <div className="mt-8 border border-zinc-900 bg-white" data-testid="website-analysis-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-zinc-200 bg-zinc-900 text-white">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              <h3 className="font-display font-bold text-lg">Website Analysis</h3>
              <span className="font-mono text-[9px] uppercase tracking-widest bg-amber-400 text-zinc-900 px-1.5 py-0.5">Admin only</span>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Input
                data-testid="website-url-input"
                value={siteUrl || quote.website_url || ""}
                onChange={(e) => setSiteUrl(e.target.value)}
                placeholder="https://clientsite.com"
                className="rounded-none h-9 bg-white text-zinc-900 text-xs w-full sm:w-[220px]"
              />
              <Button onClick={analyzeWebsite} disabled={analyzing} data-testid="analyze-website-btn" className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest text-xs h-9 whitespace-nowrap">
                {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : quote.website_report ? "Re-analyze" : "Analyze"}
              </Button>
            </div>
          </div>
          <div className="p-6">
            {analyzing ? (
              <div className="py-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                <p className="mt-3 font-mono text-xs uppercase tracking-widest text-zinc-500">Analyzing the website…</p>
              </div>
            ) : quote.website_report ? (
              <>
                {quote.website_analyzed_url && (
                  <p className="mb-4 font-mono text-xs text-zinc-500">
                    Analyzed <a href={quote.website_analyzed_url} target="_blank" rel="noreferrer" className="text-primary underline">{quote.website_analyzed_url}</a>
                    {quote.website_report_at ? ` · ${new Date(quote.website_report_at).toLocaleString()}` : ""}
                  </p>
                )}
                {(recAddonNames.length > 0 || recPackage) && (
                  <div className="mb-5 border border-primary/30 bg-primary/5 p-4" data-testid="upsell-recommendations">
                    <p className="font-mono text-xs uppercase tracking-widest text-primary">Recommended to pitch</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {recPackage && (
                        <span className="font-mono text-[11px] bg-zinc-900 text-white px-2 py-1">{recPackage.name} package</span>
                      )}
                      {recAddonNames.map((a) => (
                        <span key={a.id} className="font-mono text-[11px] bg-white border border-zinc-300 px-2 py-1">{a.name}</span>
                      ))}
                    </div>
                    <Button onClick={buildUpsellQuote} data-testid="build-upsell-btn" className="mt-3 rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest text-xs h-9">
                      <Sparkles className="h-4 w-4 mr-1.5" /> Build upsell quote
                    </Button>
                  </div>
                )}
                <div data-testid="website-report-content" className="proposal-md text-sm text-zinc-800" dangerouslySetInnerHTML={{ __html: renderMarkdown(quote.website_report) }} />
              </>
            ) : (
              <div className="py-10 text-center">
                <Globe className="h-8 w-8 text-zinc-300 mx-auto" />
                <p className="mt-3 text-zinc-600">{quote.website_url ? "Run an AI audit of the client's existing website." : "Enter the client's website URL above to run an AI audit."}</p>
                <p className="mt-1 font-mono text-xs text-zinc-400">SEO, UX, tech signals, content gaps & services to pitch.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
